import { Box, Typography, Chip, Stack, Alert } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { GridColDef, GridRenderCellParams, GridRowParams } from "@mui/x-data-grid";
import { useState, useMemo } from "react";
import { useStableQuery } from "../hooks/useStableQuery";
import { api } from "../../../convex/_generated/api";
import { useCycleFilter, ALL_CYCLES } from "../cycleFilterContext";
import { DataTableShell } from "../components/table/DataTableShell";
import { DuplicateReviewModal } from "../components/viewer/DuplicateReviewModal";

function formatCurrency(amount: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
}

function formatDate(isoDate: string) {
  return new Date(isoDate).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Row shape served by `transactions:listDuplicatesWithOriginal` —
 * each duplicate row carries the candidate "original" rows that
 * matched on (cycle, sourceFile, date, amount, canonicalVendor).
 */
type DuplicateRow = {
  id: string;
  _id: string;
  duplicate: any;
  originals: any[];
  // Flat fields hoisted onto the row for sorting/filtering in the grid.
  date: string;
  description: string;
  amount: number;
  vendorName: string | undefined;
  originalCount: number;
};

const columns: GridColDef<DuplicateRow>[] = [
  {
    field: "date",
    headerName: "Date",
    width: 120,
    renderCell: (p: GridRenderCellParams) => formatDate(p.value),
  },
  {
    field: "description",
    headerName: "Description",
    flex: 2,
    minWidth: 240,
    renderCell: (p: GridRenderCellParams) => (
      <Typography
        variant="body2"
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%",
        }}
      >
        {p.value}
      </Typography>
    ),
  },
  {
    field: "vendorName",
    headerName: "Vendor",
    width: 180,
    renderCell: (p) => p.value ?? "—",
  },
  {
    field: "amount",
    headerName: "Amount",
    width: 130,
    type: "number",
    renderCell: (p: GridRenderCellParams) => (
      <Typography
        variant="body2"
        sx={{
          color: p.value < 0 ? "error.main" : "success.main",
          fontWeight: 500,
        }}
      >
        {formatCurrency(p.value)}
      </Typography>
    ),
  },
  {
    field: "originalCount",
    headerName: "Original match",
    width: 140,
    renderCell: (p: GridRenderCellParams<DuplicateRow>) => {
      const n = p.value as number;
      if (n === 0) {
        return <Chip size="small" color="error" variant="outlined" label="not found" />;
      }
      if (n === 1) {
        return <Chip size="small" color="success" variant="outlined" label="1 match" />;
      }
      return <Chip size="small" color="warning" variant="outlined" label={`${n} matches`} />;
    },
  },
];

export function Duplicates() {
  const { selectedCycleId } = useCycleFilter();
  const [openRow, setOpenRow] = useState<DuplicateRow | null>(null);

  const data = useStableQuery(
    api.transactions.listDuplicatesWithOriginal,
    {
      reconciliationCycleId:
        selectedCycleId !== ALL_CYCLES ? (selectedCycleId as any) : undefined,
      limit: 500,
    },
    [],
  ) as { duplicate: any; originals: any[] }[] | null;

  const loading = data === undefined;

  const rows: DuplicateRow[] = useMemo(() => {
    if (!data) return [];
    return data.map((entry) => {
      const d = entry.duplicate;
      return {
        id: d._id,
        _id: d._id,
        duplicate: d,
        originals: entry.originals,
        date: d.date,
        description: d.description ?? "",
        amount: d.amount ?? 0,
        vendorName: d.vendorName,
        originalCount: entry.originals.length,
      };
    });
  }, [data]);

  return (
    <Box data-tour="page-title-duplicates">
      <DataTableShell<DuplicateRow>
        title="Duplicate Transactions"
        subtitle="Bank transactions flagged as duplicates during QB import. Click a row to compare against the original and edit if it's actually a separate charge."
        rows={rows}
        columns={columns}
        loading={loading}
        itemLabel="duplicate"
        emptyTitle="No duplicates detected"
        emptyDescription="Every imported transaction in this cycle is unique — nothing flagged for re-review."
        emptyIcon={<ContentCopyIcon sx={{ fontSize: 56, color: "text.disabled" }} />}
        notice={
          rows.some((r) => r.originalCount === 0) ? (
            <Alert severity="warning" variant="outlined">
              Some duplicates have no candidate original — the original row was
              likely deleted by a recovery sweep. Reclassify those manually after
              confirming with the bank statement.
            </Alert>
          ) : undefined
        }
        gridProps={{
          onRowClick: (params: GridRowParams<DuplicateRow>) => setOpenRow(params.row),
          getRowClassName: () => "clickable-row",
          sx: {
            "& .clickable-row": { cursor: "pointer" },
          },
        }}
      />

      <DuplicateReviewModal
        row={openRow}
        onClose={() => setOpenRow(null)}
      />

      <Stack direction="row" spacing={1} sx={{ mt: 2, color: "text.secondary" }}>
        <Typography variant="caption">
          Dedup rule: same cycle + same statement file + same date + same amount
          + same canonical vendor (vendor name OR first word of description).
        </Typography>
      </Stack>
    </Box>
  );
}
